import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import apiClient from "../../services/apiClient";

export const CreateModuleModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    text_content: '',
    youtube_url: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = { ...formData };
      if (!submitData.youtube_url) {
        submitData.youtube_url = null;
      }
      await apiClient.post('/admin/modules', submitData);
      toast.success('Module created successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create module');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card p-4 sm:p-6 w-full max-w-2xl my-4 sm:my-0" style={{ maxHeight: 'calc(100vh - 32px)' }}>
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Create New Module</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Title</label>
            <input type="text" value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="input-field" required />
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Description</label>
            <input type="text" value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field" required />
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">YouTube URL (optional)</label>
            <input type="url" value={formData.youtube_url}
              onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
              className="input-field" placeholder="https://www.youtube.com/watch?v=..." />
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Content</label>
            <textarea value={formData.text_content}
              onChange={(e) => setFormData({...formData, text_content: e.target.value})}
              className="input-field h-40 resize-none" placeholder="Enter the module content..." required />
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 pb-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-sage-500 hover:bg-sage-600 text-white">
              {loading ? 'Creating...' : 'Create Module'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export const EditModuleModal = ({ module, onClose, onSuccess }) => {
  // Local state for module data that can be updated without closing modal
  const [currentModule, setCurrentModule] = useState(module);
  const [formData, setFormData] = useState({
    title: module.title,
    description: module.description,
    text_content: module.text_content,
    youtube_url: module.youtube_url || '',
    audio_downloadable: module.audio_downloadable || false,
    is_active: module.is_active
  });
  const [loading, setLoading] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingDownload, setUploadingDownload] = useState(false);
  const [uploadingDownload2, setUploadingDownload2] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Refresh module data without closing modal
  const refreshModuleData = async () => {
    try {
      const response = await apiClient.get(`/admin/modules/${module.id}`);
      setCurrentModule(response.data);
    } catch (error) {
      console.error('Failed to refresh module data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = { ...formData };
      if (!submitData.youtube_url) submitData.youtube_url = null;
      await apiClient.put(`/admin/modules/${module.id}`, submitData);
      toast.success('Module updated successfully');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update module');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      await apiClient.post(`/admin/modules/${module.id}/image`, uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Bild hochgeladen');
      await refreshModuleData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm('Bild wirklich löschen?')) return;
    try {
      await apiClient.delete(`/admin/modules/${module.id}/image`);
      toast.success('Bild gelöscht');
      await refreshModuleData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAudio(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      await apiClient.post(`/admin/modules/${module.id}/audio`, uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Audio uploaded successfully');
      await refreshModuleData();
    } catch (error) {
      toast.error('Failed to upload audio');
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleDownloadUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDownload(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      await apiClient.post(`/admin/modules/${module.id}/download`, uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Download file uploaded successfully');
      await refreshModuleData();
    } catch (error) {
      toast.error('Failed to upload download file');
    } finally {
      setUploadingDownload(false);
    }
  };

  const handleDeleteAudio = async () => {
    if (!window.confirm('Audio-Datei wirklich löschen?')) return;
    try {
      await apiClient.delete(`/admin/modules/${module.id}/audio`);
      toast.success('Audio deleted successfully');
      await refreshModuleData();
    } catch (error) {
      toast.error('Failed to delete audio');
    }
  };

  const handleDeleteDownload = async () => {
    if (!window.confirm('Download-Datei 1 wirklich löschen?')) return;
    try {
      await apiClient.delete(`/admin/modules/${module.id}/download`);
      toast.success('Download file deleted successfully');
      await refreshModuleData();
    } catch (error) {
      toast.error('Failed to delete download file');
    }
  };

  const handleDownloadUpload2 = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingDownload2(true);
    const uploadData = new FormData();
    uploadData.append('file', file);
    try {
      await apiClient.post(`/admin/modules/${module.id}/download2`, uploadData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Download file 2 uploaded successfully');
      await refreshModuleData();
    } catch (error) {
      toast.error('Failed to upload download file 2');
    } finally {
      setUploadingDownload2(false);
    }
  };

  const handleDeleteDownload2 = async () => {
    if (!window.confirm('Download-Datei 2 wirklich löschen?')) return;
    try {
      await apiClient.delete(`/admin/modules/${module.id}/download2`);
      toast.success('Download file 2 deleted successfully');
      await refreshModuleData();
    } catch (error) {
      toast.error('Failed to delete download file 2');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="card p-4 sm:p-6 w-full max-w-2xl my-4 sm:my-0" style={{ maxHeight: 'calc(100vh - 32px)' }}>
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
        <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Edit Module</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Title</label>
            <input type="text" value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="input-field" required />
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Description</label>
            <input type="text" value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field" required />
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Modul-Bild</label>
            <div className="flex items-center space-x-3">
              <input type="file" accept="image/*" onChange={handleImageUpload} className="input-field" disabled={uploadingImage} />
              {uploadingImage && <div className="spinner"></div>}
            </div>
            {currentModule.image_filename && (
              <div className="flex items-center justify-between mt-2 bg-blue-50 p-2 rounded">
                <div className="flex items-center space-x-3">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${currentModule.image_filename}`} alt="Preview" className="w-16 h-16 object-cover rounded" />
                  <span className="text-sm text-blue-600 break-all">{currentModule.image_filename}</span>
                </div>
                <Button type="button" size="sm" variant="outline" onClick={handleDeleteImage} className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0 ml-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Audio File</label>
            <div className="flex items-center space-x-3">
              <input type="file" accept="audio/*" onChange={handleAudioUpload} className="input-field" disabled={uploadingAudio} />
              {uploadingAudio && <div className="spinner"></div>}
            </div>
            {currentModule.audio_filename && (
              <div className="flex items-center justify-between mt-2 bg-sage-50 p-2 rounded">
                <span className="text-sm text-sage-600 break-all">Current: {currentModule.audio_filename}</span>
                <Button type="button" size="sm" variant="outline" onClick={handleDeleteAudio} className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0 ml-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
            {currentModule.audio_filename && (
              <div className="flex items-center space-x-3 mt-3">
                <input type="checkbox" id="audio_downloadable" checked={formData.audio_downloadable}
                  onChange={(e) => setFormData({...formData, audio_downloadable: e.target.checked})}
                  className="rounded border-sage-300" />
                <label htmlFor="audio_downloadable" className="text-charcoal-700 text-sm font-medium">Audio Download erlauben</label>
              </div>
            )}
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Download File (PDF, etc.)</label>
            <div className="flex items-center space-x-3">
              <input type="file" onChange={handleDownloadUpload} className="input-field" disabled={uploadingDownload} />
              {uploadingDownload && <div className="spinner"></div>}
            </div>
            {currentModule.download_filename && (
              <div className="flex items-center justify-between mt-2 bg-green-50 p-2 rounded">
                <span className="text-sm text-green-600 break-all">Datei 1: {currentModule.download_filename}</span>
                <Button type="button" size="sm" variant="outline" onClick={handleDeleteDownload} className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0 ml-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Download File 2 (optional)</label>
            <div className="flex items-center space-x-3">
              <input type="file" onChange={handleDownloadUpload2} className="input-field" disabled={uploadingDownload2} />
              {uploadingDownload2 && <div className="spinner"></div>}
            </div>
            {currentModule.download_filename_2 && (
              <div className="flex items-center justify-between mt-2 bg-green-50 p-2 rounded">
                <span className="text-sm text-green-600 break-all">Datei 2: {currentModule.download_filename_2}</span>
                <Button type="button" size="sm" variant="outline" onClick={handleDeleteDownload2} className="text-red-600 border-red-200 hover:bg-red-50 flex-shrink-0 ml-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">YouTube URL (optional)</label>
            <div className="flex items-center space-x-3">
              <input type="url" value={formData.youtube_url}
                onChange={(e) => setFormData({...formData, youtube_url: e.target.value})}
                className="input-field flex-1" placeholder="https://www.youtube.com/watch?v=..." />
              {formData.youtube_url && (
                <Button type="button" size="sm" variant="outline" onClick={() => setFormData({...formData, youtube_url: ''})}
                  className="text-red-600 border-red-200 hover:bg-red-50" title="YouTube URL löschen">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            {formData.youtube_url && <p className="text-sm text-red-600 mt-1">Aktuell: {formData.youtube_url.substring(0, 50)}...</p>}
          </div>

          <div>
            <label className="block text-charcoal-700 text-sm font-medium mb-2">Content</label>
            <textarea value={formData.text_content}
              onChange={(e) => setFormData({...formData, text_content: e.target.value})}
              className="input-field h-40 resize-none" required />
          </div>

          <div className="flex items-center space-x-3">
            <input type="checkbox" id="is_active" checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="rounded border-sage-300" />
            <label htmlFor="is_active" className="text-charcoal-700 text-sm font-medium">Active Module</label>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 pb-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-sage-500 hover:bg-sage-600 text-white">
              {loading ? 'Updating...' : 'Update Module'}
            </Button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};
